import {
  formatDate,
  formatInTimezone,
  formatTime,
} from '@backoffice-frontend/common'
import { WorkOrderStatus } from '@backoffice-frontend/graphql'
import { useGlobalRegion } from '@backoffice-frontend/service-area-picker'
import { isBefore, startOfDay } from 'date-fns'

const MIDNIGHT = '00:00'

/** Only work orders that are still actionable can be overdue. */
const OVERDUE_STATUSES: WorkOrderStatus[] = [
  WorkOrderStatus.Open,
  WorkOrderStatus.InProgress,
]

type WorkOrderDueAtInfo = {
  dueAt?: string | Date | null
  status?: WorkOrderStatus | null
}

/**
 * A work order is overdue as soon as its due instant has been reached, as long as
 * it is still open or in progress.
 *
 * Both sides are absolute instants, so this is timezone independent: a work order
 * without a time (due at 00:00 in the region timezone) turns overdue at the start
 * of its due date.
 */
export const isWorkOrderOverdue = (
  { dueAt, status }: WorkOrderDueAtInfo,
  now: Date = new Date(),
): boolean => {
  if (!dueAt || !status || !OVERDUE_STATUSES.includes(status)) return false

  return new Date(dueAt).getTime() <= now.getTime()
}

/**
 * Formats a due instant in the given timezone as `31/10/2026, 08:00`. The time part
 * is omitted when it is midnight in that timezone, because work orders created
 * without a time are stored as the start of their due date.
 */
export const formatWorkOrderDueAt = (
  dueAt: string | Date,
  timeZoneId: string,
): string => {
  const date = formatInTimezone(dueAt, timeZoneId, formatDate)
  const time = formatInTimezone(dueAt, timeZoneId, formatTime)

  return time === MIDNIGHT ? date : `${date}, ${time}`
}

export const noEarlierThanToday = (now: Date = new Date()): Date =>
  startOfDay(now)

export const isDueAtInPast = (
  dueAt: Date | null,
  now: Date = new Date(),
): boolean => dueAt !== null && isBefore(dueAt, startOfDay(now))

/**
 * Resolves the timezone work order due instants are displayed in and interpreted
 * with: the selected region's, falling back to the browser's.
 */
export const useWorkOrderDueAtTimeZone = (): string => {
  const [region] = useGlobalRegion()

  return region?.timeZoneId ?? Intl.DateTimeFormat().resolvedOptions().timeZone
}
